import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getOrderList,
  fetchPaymentList,
  submitPayment,
  activateQR,
  deactivateQR,
  downloadOrderZIP,
  getOrderDetailsWithGifts,
  updateOrderDownloadStatus,
  downloadOrderFile,
  getOrderQrStatusExcel,
} from "../../redux/actions/orderActions";
import { getQrDesigns } from "../../redux/actions/qrDesignActions";
import DashboardLayout from "../../Pages/dashboard/DashboardLayout";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaDownload } from "react-icons/fa";
import { toast } from "react-toastify";
import { FullScreenLoader } from "../Components/Loader";
import MultiSelectDropdown from "../../Pages/Components/MultiSelectDropdown";
import SearchBar from "../../Pages/Components/SearchBar";
import dayjs from "dayjs";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FiDownload, FiTag } from "react-icons/fi";
import { AiOutlineFilePdf, AiOutlineFileZip } from "react-icons/ai";
import axiosInstance from "../../api/axiosInstance";

// ─── Paper Size Presets (mm) — FRONTEND ONLY, never sent to backend ───────────
const PAPER_SIZES = {
  Custom: { label: "Custom", width: null, height: null },
  A5_Portrait: { label: "A5 Portrait", width: 148, height: 210 },
  A5_Landscape: { label: "A5 Landscape", width: 210, height: 148 },
  A4_Portrait: { label: "A4 Portrait", width: 210, height: 297 },
  A4_Landscape: { label: "A4 Landscape", width: 297, height: 210 },
  A3_Portrait: { label: "A3 Portrait", width: 297, height: 420 },
  A3_Landscape: { label: "A3 Landscape", width: 420, height: 297 },
};

const DEFAULT_QR_CELL_MM = 25; // each QR occupies ~25x25 mm
const MARGIN_MM = 10; // page margin each side

/**
 * Given paper dimensions, calculate how many QR rows & cols fit.
 * Pure frontend helper — result is written into qr_count state.
 */
const calcGridForPaper = (widthMm, heightMm, cellMm = DEFAULT_QR_CELL_MM) => {
  if (widthMm === 420 && heightMm === 297) {
    return { rows: 7, cols: 7 };
  }
  const cols = Math.max(1, Math.floor((widthMm - MARGIN_MM * 2) / cellMm));
  const rows = Math.max(1, Math.floor((heightMm - MARGIN_MM * 2) / cellMm));
  return { rows, cols };
};

const MyOrders = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { userInfo } = useSelector((state) => state.auth);
  const { orders, loading, error } = useSelector((state) => state.orderList);
  const { payments } = useSelector((state) => state.paymentList);
  const { loading: downloading, error: downloadError } = useSelector(
    (state) => state.orderDownload || {},
  );

  // ─── General state ────────────────────────────────────────────────────────────
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivationOrder, setDeactivationOrder] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedActivationStatuses, setSelectedActivationStatuses] = useState(
    [],
  );
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [upiError, setUpiError] = useState("");
  const [downloadingQrStatus, setDownloadingQrStatus] = useState({});

  const orderType = (
    userInfo?.user_order_type ||
    userInfo?.data?.user_order_type ||
    userInfo?.order_type ||
    userInfo?.user_type ||
    "loyality"
  ).toLowerCase();

  const isLoyaltyUser = orderType === "loyality" || orderType === "loyalty";

  const getMediaUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    const base = axiosInstance.defaults.baseURL?.replace(/\/$/, "") || "";
    return `${base}${path}`;
  };

  // ─── Download / order detail state ───────────────────────────────────────────
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(null);
  const [downloadingFormat, setDownloadingFormat] = useState(null);
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
  const [updatingDownloadStatus, setUpdatingDownloadStatus] = useState({});

  // ─── Sticker modal state ──────────────────────────────────────────────────────
  const [showStickerModal, setShowStickerModal] = useState(false);
  const [stickerData, setStickerData] = useState({
    orderId: "",
    base_image: null,
    bottom_percent: "0",
    right_percent: "0",
    rotation_percent: "0",
    qr_size_percent: "15",
    qr_count: "", // stored as "rows*cols" e.g. "8*5"
    card_width: "", // ← add
    card_height: "",
    page_height: "30.48",
    page_width: "45.72",
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [imageDimensions, setImageDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [qrDesigns, setQrDesigns] = useState([]);
  const [loadingQrDesigns, setLoadingQrDesigns] = useState(false);
  const [selectedQrDesign, setSelectedQrDesign] = useState(null);

  // ─── Paper size state (frontend only) ────────────────────────────────────────
  const [selectedPaperSize, setSelectedPaperSize] = useState("Custom");
  const [paperWidth, setPaperWidth] = useState("");
  const [paperHeight, setPaperHeight] = useState("");

  const { access_modules } = useSelector((state) => state.dealerAccess);
  const modulePermissions = Array.isArray(access_modules?.data)
    ? access_modules.data
    : [];
  console.log(
    "dealerAccess state:",
    useSelector((state) => state.dealerAccess),
  );
  const hasModuleAccess = (alias) => {
    const mod = modulePermissions.find((m) => m.alias === alias);
    if (!mod) return false;
    return mod.is_create || mod.is_update || mod.is_read || mod.is_delete;
  };
  const [qrRowsVal, qrColsVal] = (() => {
    const parts = (stickerData.qr_count || "").split("*");
    return [parts[0] ?? "", parts[1] ?? ""];
  })();

  // ─── Activation / payment state ──────────────────────────────────────────────
  const [activationData, setActivationData] = useState({
    orderId: "",
    startDate: "",
    endDate: "",
  });
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const getLocalDateTime = () => {
    const now = new Date();
    return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
  };
  const [paymentData, setPaymentData] = useState({
    payment_type: "",
    utr_number: "",
    payment_date: getLocalDateTime(),
  });

  const validateUpi = (v) => /^[\w.\-]{2,256}@[a-zA-Z]{2,64}$/.test(v);

  const orderTypeConfig = {
    cashback: { label: "Create Cashback Coupon", type: "cashback" },
    promotional: { label: "Create Promotional Coupon", type: "promotion" },
    loyality: { label: "Create Loyality Coupon", type: "loyality" },
    loyalty: { label: "Create Loyality Coupon", type: "loyality" },
  };
  const buttonConfig =
    orderTypeConfig[orderType] || orderTypeConfig["loyality"];
  const handleDownloadQrStatusExcel = async (orderId) => {
    if (!orderId || !userInfo?.access_token) {
      toast.error("Missing required information");
      return;
    }
    setDownloadingQrStatus((prev) => ({ ...prev, [orderId]: true }));
    try {
      const result = await dispatch(
        getOrderQrStatusExcel(orderId, userInfo.access_token),
      );
      if (result?.success && result?.download_url) {
        downloadFile(result.download_url); // reuses your existing helper
        toast.success(
          result.message || "QR status report generated successfully!",
        );
      } else {
        toast.error(result?.error || "Failed to generate QR status report.");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to generate QR status report.");
    } finally {
      setDownloadingQrStatus((prev) => ({ ...prev, [orderId]: false }));
    }
  };
  // ─── Effects ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (userInfo?.access_token) {
      dispatch(getOrderList(userInfo.access_token));
      dispatch(fetchPaymentList(userInfo.access_token));
    }
  }, [dispatch, userInfo]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        showDownloadDropdown &&
        downloadingFormat === null &&
        !e.target.closest(".download-dropdown-container") &&
        !e.target.closest(".download-options-modal")
      )
        setShowDownloadDropdown(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDownloadDropdown, downloadingFormat]);

  // ─── Handlers ─────────────────────────────────────────────────────────────────
  const handleActivateSubmit = async () => {
    const { orderId, startDate: sd, endDate: ed } = activationData;
    if (!sd) {
      alert("Please select a start date.");
      return;
    }
    if (!isLoyaltyUser && !ed) {
      alert("Please select an end date.");
      return;
    }
    const formatDDMMYYYY = (dateObj) => {
      const d = typeof dateObj === "string" ? new Date(dateObj) : dateObj;
      return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
    };
    const payload = {
      order_id: orderId,
      start_date: formatDDMMYYYY(sd),
      ...(!isLoyaltyUser && ed ? { end_date: formatDDMMYYYY(ed) } : {}),
    };
    try {
      await dispatch(activateQR(payload, userInfo.access_token));
      await dispatch(getOrderList(userInfo.access_token));
      setShowActivateModal(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeactivateClick = (order) => {
    setDeactivationOrder(order);
    setShowDeactivateModal(true);
  };

  const handleConfirmDeactivate = () => {
    if (deactivationOrder) {
      dispatch(
        deactivateQR(deactivationOrder.order_id, userInfo.access_token),
      ).then(() => {
        dispatch(getOrderList(userInfo.access_token));
        setShowDeactivateModal(false);
        setDeactivationOrder(null);
      });
    }
  };

  const toggleDownloadDropdown = (orderId, e) => {
    e.stopPropagation();
    setShowDownloadDropdown(showDownloadDropdown === orderId ? null : orderId);
  };

  const handleDownloadOption = async (orderId, format) => {
    if (!orderId || !userInfo?.access_token) {
      toast.error("Missing required information");
      setShowDownloadDropdown(null);
      return;
    }
    setDownloadingFormat(format);
    if (format === "sticker") {
      setShowDownloadDropdown(null);
      setDownloadingFormat(null);
      setStickerData({
        orderId,
        base_image: null,
        bottom_percent: "10",
        right_percent: "50",
        rotation_percent: "0",
        qr_size_percent: "15",
        qr_count: "",
        card_width: "",
        card_height: "",
        page_height: "", // ← ADD with default value
        page_width: "", // ← ADD with default value
      });
      setImagePreview(null);
      setSelectedQrDesign(null);
      setSelectedPaperSize("Custom");
      setPaperWidth("");
      setPaperHeight("");
      setShowStickerModal(true);
      if (userInfo?.access_token) {
        setLoadingQrDesigns(true);
        try {
          const filters = { is_active: true };
          if (userInfo?.user_id) filters.client_id = userInfo.user_id;
          const result = await dispatch(
            getQrDesigns(filters, userInfo.access_token),
          );
          if (result?.qr_designs) setQrDesigns(result.qr_designs);
          else if (result?.data) setQrDesigns(result.data);
        } catch (e) {
          console.error(e);
        } finally {
          setLoadingQrDesigns(false);
        }
      }
    } else {
      try {
        // const result = await dispatch(downloadOrderZIP(orderId, userInfo.access_token, format));
        // setShowDownloadDropdown(null); setDownloadingFormat(null);
        // if (result?.success) { toast.success(`File "${result.filename}" downloaded successfully!`); dispatch(getOrderList(userInfo.access_token)); }
        // else if (result?.error) toast.error(result.error);
        // else { toast.success("File download initiated"); dispatch(getOrderList(userInfo.access_token)); }
        const result = await dispatch(
          downloadOrderZIP(orderId, userInfo.access_token, format),
        );
        setShowDownloadDropdown(null);
        setDownloadingFormat(null);

        if (result?.success) {
          if (result?.async || result?.status === 202) {
            // ✅ Background job started — no file yet
            toast.info(
              result?.message ||
                `${format.toUpperCase()} generation started. Please check back later for the download link.`,
            );
          } else if (result?.filename) {
            // ✅ Immediate download with filename
            toast.success(`File "${result.filename}" downloaded successfully!`);
          } else {
            // ✅ Success but no filename (shouldn't happen, safe fallback)
            toast.success("File download initiated successfully.");
          }
          dispatch(getOrderList(userInfo.access_token));
        } else if (result?.error) {
          toast.error(result.error);
        } else {
          toast.success("File download initiated.");
          dispatch(getOrderList(userInfo.access_token));
        }
      } catch (error) {
        setShowDownloadDropdown(null);
        setDownloadingFormat(null);
        toast.error(error?.message || "Failed to download file.");
      }
    }
  };
  const downloadFile = (url) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = ""; // optionally, set a filename here
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownload = async () => {
    try {
      const response = await dispatch(
        getOrderList(userInfo.access_token, { download: true }),
      );
      console.log("API response:", response);
      // const fileUrl = response?.download_url;
      const fileUrl = response?.data?.download_url;
      if (fileUrl) {
        downloadFile(fileUrl);
      } else {
        console.warn("No download URL in response");
      }
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  const handleViewOrderDetails = async (orderId) => {
    setLoadingOrderDetails(true);
    setShowOrderDetailsModal(true);
    try {
      const result = await dispatch(
        getOrderDetailsWithGifts(orderId, userInfo.access_token),
      );
      if (result?.success) setOrderDetails(result.data);
      else {
        alert(result?.error || "Failed to load order details");
        setShowOrderDetailsModal(false);
      }
    } catch (error) {
      alert(
        error.response?.data?.message ||
          error.message ||
          "Failed to load order details",
      );
      setShowOrderDetailsModal(false);
    } finally {
      setLoadingOrderDetails(false);
    }
  };

  const handleUpdateDownloadStatus = async (orderId, currentStatus) => {
    if (!userInfo?.access_token) {
      toast.error("Authentication token missing");
      return;
    }
    const newStatus = !currentStatus;
    setUpdatingDownloadStatus((prev) => ({ ...prev, [orderId]: true }));
    try {
      await dispatch(
        updateOrderDownloadStatus(orderId, newStatus, userInfo.access_token),
      );
      toast.success(
        `Download status updated to ${newStatus ? "Downloaded" : "Not Downloaded"}`,
      );
      dispatch(getOrderList(userInfo.access_token));
    } catch (error) {
      toast.error(error.message || "Failed to update download status");
    } finally {
      setUpdatingDownloadStatus((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setStickerData((prev) => ({ ...prev, base_image: file, qr_count: "" }));
      setSelectedQrDesign(null);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        const img = new Image();
        img.onload = () =>
          setImageDimensions({ width: img.width, height: img.height });
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };
  // const handleExport = async () => {
  //   if (filteredOrders.length === 0) {
  //     toast.warning("No orders to export.");
  //     return;
  //   }

  //   const results = await Promise.all(
  //     filteredOrders.map((order) =>
  //       dispatch(getOrderDetailsWithGifts(order.order_id, userInfo.access_token, true))
  //     )
  //   );

  //   console.log("Export results:", results);
  // };

  const handleExport = async () => {
    if (filteredOrders.length === 0) {
      toast.warning("No orders to export.");
      return;
    }

    const result = await dispatch(
      getOrderDetailsWithGifts(null, userInfo.access_token, true),
    );

    if (result?.success && result?.download_url) {
      window.open(result.download_url, "_blank");
      toast.success("Export downloaded successfully!");
    } else {
      toast.error("Failed to export orders.");
    }
  };
  const handleQrDesignSelect = async (qrDesign) => {
    setSelectedQrDesign(qrDesign);
    if (qrDesign.qr_image) setImagePreview(getMediaUrl(qrDesign.qr_image));
    if (qrDesign.qr_image) {
      try {
        let imageUrl = getMediaUrl(qrDesign.qr_image);
        let blob;
        let contentType = "image/png";
        try {
          const response = await fetch(imageUrl, {
            method: "GET",
            mode: "cors",
            cache: "no-cache",
          });
          if (!response.ok)
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          blob = await response.blob();
          contentType =
            response.headers.get("content-type") || blob.type || "image/png";
          if (!blob || blob.size === 0) throw new Error("Received empty blob");
        } catch (fetchError) {
          console.warn("Direct fetch failed, trying canvas:", fetchError);
          const img = new Image();
          img.crossOrigin = "anonymous";
          let imgW = 0,
            imgH = 0;
          await new Promise((resolve, reject) => {
            img.onload = () => {
              try {
                imgW = img.naturalWidth;
                imgH = img.naturalHeight;
                const canvas = document.createElement("canvas");
                canvas.width = imgW;
                canvas.height = imgH;
                canvas.getContext("2d").drawImage(img, 0, 0);
                canvas.toBlob((blobData) => {
                  if (blobData) {
                    blob = blobData;
                    contentType = blobData.type || "image/png";
                    resolve();
                  } else reject(new Error("Canvas blob failed"));
                }, "image/png");
              } catch (e) {
                reject(new Error(`Canvas: ${e.message}`));
              }
            };
            img.onerror = () => reject(new Error("Image load failed"));
            img.src = imageUrl;
          });
          if (imgW > 0 && imgH > 0)
            setImageDimensions({ width: imgW, height: imgH });
        }
        const ext =
          contentType.includes("jpeg") || contentType.includes("jpg")
            ? "jpg"
            : "png";
        const file = new File([blob], `qr_design_${qrDesign.id}.${ext}`, {
          type: contentType,
        });
        if (!imageDimensions.width || !imageDimensions.height) {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () =>
            setImageDimensions({
              width: img.naturalWidth,
              height: img.naturalHeight,
            });
          img.src = getMediaUrl(qrDesign.qr_image);
        }
        setStickerData((prev) => ({
          ...prev,
          base_image: file,
          bottom_percent: qrDesign.bottom_percent || "10",
          right_percent: qrDesign.right_percent || "50",
          rotation_percent: qrDesign.rotation_percent || "0",
          qr_size_percent: qrDesign.qr_size_percent || "15",
          qr_count: qrDesign.qr_count || "",
          card_width: qrDesign.card_width || "", // ← add
          card_height: qrDesign.card_height || "",
          page_height: qrDesign.page_height || "",
          page_width: qrDesign.page_width || "",
        }));
        toast.success("QR design loaded successfully!");
      } catch (error) {
        console.error("Error loading QR design image:", error);
        toast.error(`Failed to load QR design image: ${error.message}`);
        setStickerData((prev) => ({
          ...prev,
          bottom_percent: qrDesign.bottom_percent || "10",
          right_percent: qrDesign.right_percent || "50",
          rotation_percent: qrDesign.rotation_percent || "0",
          qr_size_percent: qrDesign.qr_size_percent || "15",
          qr_count: qrDesign.qr_count || "",
        }));
      }
    } else {
      setStickerData((prev) => ({
        ...prev,
        bottom_percent: qrDesign.bottom_percent || "10",
        right_percent: qrDesign.right_percent || "50",
        rotation_percent: qrDesign.rotation_percent || "0",
        qr_size_percent: qrDesign.qr_size_percent || "15",
        qr_count: qrDesign.qr_count || "",
      }));
    }
  };

  // ─── Paper size handler — FRONTEND ONLY ───────────────────────────────────────
  // Selecting a preset auto-calculates rows/cols. Nothing paper-related is sent to backend.
  const handlePaperSizeChange = (sizeKey) => {
    setSelectedPaperSize(sizeKey);
    const preset = PAPER_SIZES[sizeKey];
    if (preset && preset.width !== null) {
      setPaperWidth(String(preset.width));
      setPaperHeight(String(preset.height));
      const { rows, cols } = calcGridForPaper(preset.width, preset.height);
      setStickerData((prev) => ({ ...prev, qr_count: `${rows}*${cols}` }));
      toast.info(
        `Auto-set grid: ${rows} rows x ${cols} cols for ${preset.label}`,
      );
    } else {
      setPaperWidth("");
      setPaperHeight("");
      // Custom: keep whatever rows/cols the user already had
    }
  };

  // Re-calc grid when user types custom width/height
  const handleCustomDimensionChange = (field, value) => {
    const numVal = value.replace(/[^0-9]/g, "");
    const newW = field === "width" ? Number(numVal) : Number(paperWidth);
    const newH = field === "height" ? Number(numVal) : Number(paperHeight);
    if (field === "width") setPaperWidth(numVal);
    if (field === "height") setPaperHeight(numVal);
    setSelectedPaperSize("Custom");
    if (newW > 0 && newH > 0) {
      const { rows, cols } = calcGridForPaper(newW, newH);
      setStickerData((prev) => ({ ...prev, qr_count: `${rows}*${cols}` }));
    }
  };

  // ─── Sticker submit — paper size NOT sent to backend ──────────────────────────
  const handleStickerSubmit = async () => {
    if (!stickerData.base_image) {
      toast.error("Please upload an image first.");
      return;
    }
    const formData = new FormData();
    formData.append("file_type", "sticker");
    formData.append("base_image", stickerData.base_image);
    formData.append("bottom_percent", stickerData.bottom_percent);
    formData.append("right_percent", stickerData.right_percent);
    formData.append("rotation_percent", stickerData.rotation_percent);
    formData.append("qr_size_percent", stickerData.qr_size_percent);
    // if (stickerData.qr_count) formData.append("qr_count", stickerData.qr_count);
    formData.append("qr_count", stickerData.qr_count);
    formData.append("card_width", stickerData.card_width || "");
    formData.append("card_height", stickerData.card_height || "");
    formData.append("page_height", stickerData.page_height || "");
    formData.append("page_width", stickerData.page_width || "");
    // paper_width_mm / paper_height_mm intentionally NOT appended — frontend use only

    // console.log("Sticker Payload:");
    // for (let [key, val] of formData.entries()) console.log(`  ${key}:`, val);

    const payloadDebug = {};
    for (let [key, val] of formData.entries()) {
      payloadDebug[key] =
        val instanceof File ? `[File: ${val.name}, size: ${val.size}]` : val;
    }
    console.log("=== STICKER PAYLOAD ===");
    console.table(payloadDebug);
    console.log(
      "card_width value:",
      stickerData.card_width,
      "| type:",
      typeof stickerData.card_width,
    );
    console.log(
      "card_height value:",
      stickerData.card_height,
      "| type:",
      typeof stickerData.card_height,
    );
    console.log(
      "page_height  value:",
      stickerData.page_height,
      "| type:",
      typeof stickerData.page_height,
    );
    console.log(
      "page_width value:",
      stickerData.page_width,
      "| type:",
      typeof stickerData.page_width,
    );
    console.log("======================");

    try {
      const result = await dispatch(
        downloadOrderZIP(
          stickerData.orderId,
          userInfo.access_token,
          "sticker",
          formData,
        ),
      );
      console.log("=== FULL RESULT ===", JSON.stringify(result, null, 2));
      console.log("result.success:", result?.success);
      console.log("result.async:", result?.async);
      console.log("result.filename:", result?.filename);
      console.log("result.file_url:", result?.file_url);
      console.log("result.status:", result?.status);
      console.log("===================");

      if (result?.success) {
        if (result.async) {
          // 202 — background job started
          toast.info(
            result.message ||
              "Sticker generation started. You'll be notified when ready.",
          );
        } else {
          toast.success(`File "${result.filename}" downloaded successfully!`);
        }
        setShowStickerModal(false);
        setSelectedQrDesign(null);
        setImagePreview(null);
        setImageDimensions({ width: 0, height: 0 });
        setSelectedPaperSize("Custom");
        setPaperWidth("");
        setPaperHeight("");
        dispatch(getOrderList(userInfo.access_token));
      }
    } catch (error) {
      toast.error(error.message || "Failed to download file.");
    }
  };

  const handleMakePaymentClick = (orderId) =>
    setSelectedOrderId(selectedOrderId === orderId ? null : orderId);

  const handlePaymentSubmit = async () => {
    if (
      !paymentData.payment_type ||
      (paymentData.payment_type === "Bank Transfer" &&
        !paymentData.utr_number) ||
      (paymentData.payment_type === "UPI" && !paymentData.upi_id) ||
      (paymentData.payment_type === "Cash" && !paymentData.person_name) ||
      !paymentData.payment_date
    ) {
      alert("Please fill in all required payment details.");
      return;
    }
    if (
      paymentData.payment_type === "UPI" &&
      !validateUpi(paymentData.upi_id)
    ) {
      alert("Please enter a valid UPI ID (e.g., name@bank)");
      return;
    }
    const inputDate = new Date(paymentData.payment_date);
    const formattedDate =
      `${String(inputDate.getDate()).padStart(2, "0")}-${String(inputDate.getMonth() + 1).padStart(2, "0")}-${inputDate.getFullYear()} ` +
      `${String(inputDate.getHours()).padStart(2, "0")}:${String(inputDate.getMinutes()).padStart(2, "0")}:00`;
    const paymentEntry = payments?.data?.find(
      (p) => p.order_id === selectedOrderId,
    );
    if (!paymentEntry) {
      alert("No matching payment found for this order.");
      return;
    }
    try {
      await dispatch(
        submitPayment(
          paymentEntry.payment_id,
          { ...paymentData, payment_date: formattedDate },
          userInfo.access_token,
        ),
      );
      await dispatch(fetchPaymentList(userInfo.access_token));
      await dispatch(getOrderList(userInfo.access_token));
      setSelectedOrderId(null);
      setPaymentData({ payment_type: "", utr_number: "", payment_date: "" });
    } catch (error) {
      alert("Error submitting payment.");
    }
  };

  // ─── Filter options ───────────────────────────────────────────────────────────
  const activationStatusOptions = [
    { value: "All", label: "All" },
    { value: "Activated", label: "Activated" },
    { value: "Deactivated", label: "Deactivated" },
  ];
  const statusOptions = [
    { value: "All", label: "All" },
    { value: "Payment Pending", label: "Payment Pending" },
    { value: "Approval Pending", label: "Approval Pending" },
    { value: "Successful", label: "Approved" },
    { value: "Pending Payment", label: "Order Approval Pending" },
  ];

  // ─── Filtered orders ──────────────────────────────────────────────────────────
  const filteredOrders =
    orders
      ?.filter((order) => {
        if (userInfo?.role_id === "6") return true;
        return (
          order.user_id === userInfo?.user_id ||
          order.client_id === userInfo?.user_id
        );
      })
      ?.filter((order) => {
        if (selectedStatuses.length === 0) return true;
        const payment = payments?.data?.find(
          (p) => p.order_id === order.order_id,
        );
        return selectedStatuses.some(({ value }) => {
          if (value === "Pending Payment") return order.status === value;
          if (value === "All") return true;
          return payment?.status === value;
        });
      })
      ?.filter((order) => {
        if (selectedActivationStatuses.length === 0) return true;
        return selectedActivationStatuses.some(({ value }) => {
          if (value === "All") return true;
          if (value === "Activated") return order.is_active === true;
          if (value === "Deactivated")
            return order.is_active === false && order.is_approved === true;
          return true;
        });
      })
      ?.filter((order) =>
        order.order_id
          ?.toString()
          .toLowerCase()
          .includes(searchValue.trim().toLowerCase()),
      )
      ?.filter((order) => {
        if (!startDate && !endDate) return true;
        const [day, month, year] = (order.order_time?.trim() || "").split("-");
        if (!day || !month || !year) return false;
        const orderDate = dayjs(`${year}-${month}-${day}`);
        if (!orderDate.isValid()) return false;
        const start = startDate ? dayjs(startDate).startOf("day") : null;
        const end = endDate ? dayjs(endDate).endOf("day") : null;
        if (start && orderDate.isBefore(start)) return false;
        if (end && orderDate.isAfter(end)) return false;
        return true;
      }) || [];

  const totalOrders = filteredOrders.length;
  const totalQRCounts = filteredOrders.reduce(
    (sum, o) => sum + (parseInt(o.qr_quantity) || 0),
    0,
  );

  const parseQrCount = (qrCount) => {
    if (!qrCount) return null;
    const [r, c] = qrCount.split("*");
    const rows = parseInt(r?.trim()),
      cols = parseInt(c?.trim());
    if (!rows || !cols) return null;
    return { rows, cols, total: rows * cols };
  };

  // ─── JSX ─────────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <FullScreenLoader
        show={downloading}
        message="Generating sticker file..."
        variant="primary"
        blurIntensity="md"
      />
      <FullScreenLoader
        show={loading}
        message="Loading orders..."
        variant="primary"
        blurIntensity="md"
      />

      <div className="mt-2 max-w-full mx-2">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-amber-600 mb-1">My Orders</h2>
          <div className="flex space-x-2">
            <button
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 flex items-center px-4 py-2 text-sm font-medium text-white border border-amber-600 rounded-lg transition shadow-md hover:shadow-lg"
              onClick={() =>
                navigate("/dashboard/orderRequest", {
                  state: { type: buttonConfig.type },
                })
              }
            >
              <FiTag className="mr-2" /> {buttonConfig.label}
            </button>
            {/* <button className="bg-white hover:bg-amber-50 flex items-center px-4 py-2 text-sm font-medium text-gray-700 border-2 border-amber-500 rounded-lg transition shadow-sm hover:shadow-md">
              <FiDownload
              onClick={handleExport}
               className="w-4 h-4 mr-2 text-amber-600" /> Export
            </button> */}
            <button
              onClick={handleDownload}
              className="bg-white flex items-center px-4 py-2 text-sm font-medium text-black border border-gray-500 rounded-md transition"
            >
              <FiDownload className="w-4 h-4 mr-2 text-amber-500" />
              Export
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">
                  Total Orders
                </p>
                <p className="text-3xl font-bold bg-gradient-to-r from-amber-600 to-amber-800 bg-clip-text text-transparent">
                  {totalOrders}
                </p>
              </div>
              <div className="bg-gradient-to-br from-amber-400 to-amber-600 rounded-full p-4">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">
                  Total QR Counts
                </p>
                <p className="text-3xl font-bold bg-gradient-to-r from-amber-600 to-amber-800 bg-clip-text text-transparent">
                  {totalQRCounts.toLocaleString()}
                </p>
              </div>
              <div className="bg-gradient-to-br from-amber-400 to-amber-600 rounded-full p-4">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="overflow-x-auto">
          <div className="mt-0 w-full flex flex-col sm:flex-row items-center justify-start gap-4">
            <SearchBar
              value={searchValue}
              onChange={setSearchValue}
              placeholder="Search by Order ID"
            />
            {!isLoyaltyUser && (
              <MultiSelectDropdown
                className="w-1/2"
                options={statusOptions}
                selectedValues={selectedStatuses}
                setSelectedValues={setSelectedStatuses}
                placeholder="Filter by Status"
              />
            )}
            <MultiSelectDropdown
              options={activationStatusOptions}
              selectedValues={selectedActivationStatuses}
              setSelectedValues={setSelectedActivationStatuses}
              placeholder="Filter by Activation Status"
            />
            <DatePicker
              selectsRange
              startDate={startDate}
              endDate={endDate}
              onChange={(update) => setDateRange(update)}
              isClearable
              placeholderText="Select date range"
              dateFormat="dd MMM yyyy"
              className="placeholder-gray-950  px-4 py-2 border-2 border-amber-300 rounded-lg text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition"
            />
          </div>
        </div>

        {(selectedStatuses.length > 0 ||
          selectedActivationStatuses.length > 0) && (
          <div className="w-full flex gap-6 mt-2">
            {selectedStatuses.length > 0 && (
              <div>
                <div className="text-md font-bold text-gray-950 py-2">
                  Selected Status:
                </div>
                <div className="flex flex-wrap gap-4">
                  {selectedStatuses.map((s) => (
                    <span
                      key={s.value}
                      className="bg-amber-300 text-gray-950 px-3 py-2 rounded-full text-sm font-medium"
                    >
                      {s.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {selectedActivationStatuses.length > 0 && (
              <div>
                <div className="text-md font-bold text-gray-950 py-2">
                  Selected Activation Status:
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedActivationStatuses.map((s) => (
                    <span
                      key={s.value}
                      className="bg-amber-300 text-gray-950 px-3 py-2 rounded-full text-sm font-medium"
                    >
                      {s.label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Empty / error states */}
        {error && (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200 rounded-2xl p-8 max-w-md w-full text-center shadow-lg">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                Orders Not Found
              </h3>
              <p className="text-gray-600 mb-6">
                You haven't created any orders yet.
              </p>
              <button
                onClick={() =>
                  navigate("/dashboard/orderRequest", {
                    state: { type: buttonConfig.type },
                  })
                }
                className="bg-gradient-to-r from-amber-500 to-amber-600 text-white py-3 px-6 rounded-lg font-semibold transition shadow-md inline-flex items-center gap-2"
              >
                <FiTag className="w-5 h-5" /> Create Your First Order
              </button>
            </div>
          </div>
        )}

        {!loading && !error && orders.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200 rounded-2xl p-8 max-w-md w-full text-center shadow-lg">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                No Orders Found
              </h3>
              <p className="text-gray-600 mb-6">
                You haven't created any orders yet.
              </p>
              <button
                onClick={() =>
                  navigate("/dashboard/orderRequest", {
                    state: { type: buttonConfig.type },
                  })
                }
                className="bg-gradient-to-r from-amber-500 to-amber-600 text-white py-3 px-6 rounded-lg font-semibold inline-flex items-center gap-2"
              >
                <FiTag className="w-5 h-5" /> Create Your First Order
              </button>
            </div>
          </div>
        )}

        {/* ── LOYALTY TABLE ── */}
        {!loading && !error && orders.length > 0 && isLoyaltyUser && (
          <div className="mt-6 overflow-x-auto rounded-xl border-2 border-amber-200 shadow-lg">
            <table className="min-w-full bg-white text-sm text-gray-950">
              {/* {filteredOrders.length === 0 && (
                <div className="font-bold text-md text-center text-gray-950 py-6">No match found</div>
              )} */}
              {filteredOrders.length === 0 && (
                <tbody>
                  <tr>
                    <td
                      colSpan={9}
                      className="font-bold text-md text-center text-gray-950 py-6"
                    >
                      No match found
                    </td>
                  </tr>
                </tbody>
              )}
              <thead className="bg-gradient-to-r from-amber-500 to-amber-600 text-xs uppercase text-white text-center">
                <tr>
                  <th className="py-3 px-5 border-b">SI No</th>
                  <th className="py-3 px-5 border-b">Date</th>
                  <th className="py-3 px-5 border-b">Order ID</th>
                  <th className="py-3 px-5 border-b">Order Type</th>
                  <th className="py-3 px-5 border-b">Schema Name</th>
                  <th className="py-3 px-5 border-b">QR Quantity</th>
                  <th className="py-3 px-5 border-b">Status</th>
                  <th className="py-3 px-5 border-b">View</th>
                  <th className="py-3 px-5 border-b">Download</th>
                  <th className="py-3 px-5 border-b">QR Status</th>
                  {userInfo?.role_id === "6" && (
                    <th className="py-3 px-5 border-b">Download Status</th>
                  )}
                  <th className="py-3 px-5 border-b">Start Date</th>
                  <th className="py-3 px-5 border-b">End Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-300">
                {filteredOrders.map((order, idx) => (
                  <React.Fragment key={order.order_id}>
                    <tr className="text-center hover:bg-amber-50 hover:text-gray-950 hover:font-semibold">
                      <td className="px-2 py-4">{idx + 1}</td>
                      <td className="px-2 py-4">{order.order_time}</td>
                      <td className="px-2 py-4">{order.order_id}</td>
                      <td className="px-2 py-4">{order.order_type}</td>
                      <td className="px-2 py-4">
                        {order.breakdowns
                          ?.filter((b) => b.scheme)
                          .map((b) => b.scheme)
                          .join(", ") || "-"}
                      </td>
                      <td className="px-2 py-4">{order.qr_quantity}</td>
                      <td className="px-2 py-4">
                        {order.is_approved &&
                        order.is_active &&
                        order.is_expired === false ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeactivateClick(order);
                            }}
                            className="group bg-green-500 hover:bg-red-500 text-white py-1 px-3 rounded-full text-sm transition"
                          >
                            <span className="group-hover:hidden">
                              Activated
                            </span>
                            <span className="hidden group-hover:inline">
                              Deactivate
                            </span>
                          </button>
                        ) : order.is_expired === true ? (
                          <button className="bg-red-500 text-white rounded-full py-1 px-3 text-sm">
                            Expired
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActivationData({
                                orderId: order.order_id,
                                startDate: "",
                                endDate: "",
                              });
                              setShowActivateModal(true);
                            }}
                            className="bg-blue-500 hover:bg-green-500 text-white rounded-full py-1 px-3 text-sm transition"
                          >
                            Activate
                          </button>
                        )}
                      </td>
                      <td className="px-2 py-4 text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewOrderDetails(order.order_id);
                          }}
                          className="bg-amber-500 hover:bg-amber-600 text-white py-1 px-3 rounded-full text-sm transition font-medium"
                        >
                          View
                        </button>
                      </td>
                      {/* <td className="px-2 py-4 text-center">
                        {order.is_approved && order.is_active && order.is_expired === false ? (
                          <button
                            onClick={(e) => { if (!order.is_downloaded) toggleDownloadDropdown(order.order_id, e); }}
                            disabled={order.is_downloaded}
                            className={`py-1 px-3 rounded-full text-sm inline-flex items-center gap-1 ${order.is_downloaded ? "bg-gray-400 cursor-not-allowed text-white opacity-60" : "bg-indigo-500 hover:bg-indigo-600 text-white"}`}
                            title={order.is_downloaded ? "Already downloaded" : "Download QR codes"}
                          ><FaDownload /></button>
                        ) : <span className="text-gray-400">-</span>}
                      </td> */}
                      <td className="px-2 py-4 text-center">
                        {order.is_downloaded !== true &&
                        order.order_download_url ? (
                          // Real URL string — show "Click here to view"
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              dispatch(
                                downloadOrderFile(
                                  order.order_id,
                                  userInfo.access_token,
                                ),
                              );
                            }}
                            className="py-1 px-3 rounded-full text-sm inline-flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white transition"
                          >
                            Click here to View
                          </button>
                        ) : order.is_downloaded ? (
                          // is_downloaded true — disabled
                          <button
                            disabled
                            className="py-1 px-3 rounded-full text-sm inline-flex items-center gap-1 bg-gray-400 cursor-not-allowed text-white opacity-60"
                            title="Already downloaded"
                          >
                            <FaDownload />
                          </button>
                        ) : (
                          // Not downloaded — active download icon
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDownloadDropdown(order.order_id, e);
                            }}
                            className="py-1 px-3 rounded-full text-sm inline-flex items-center gap-1 bg-indigo-500 hover:bg-indigo-600 text-white"
                            title="Download QR codes"
                          >
                            <FaDownload />
                          </button>
                        )}
                      </td>

                      <td className="px-2 py-4 text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadQrStatusExcel(order.order_id);
                          }}
                          disabled={downloadingQrStatus[order.order_id]}
                          className="py-1 px-3 rounded-full text-sm inline-flex items-center gap-1 bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {downloadingQrStatus[order.order_id]
                            ? "Generating..."
                            : "QR Status"}
                        </button>
                      </td>

                      {userInfo?.role_id === "6" && (
                        <td className="px-2 py-4 text-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUpdateDownloadStatus(
                                order.order_id,
                                order.is_downloaded,
                              );
                            }}
                            disabled={updatingDownloadStatus[order.order_id]}
                            className={`py-1 px-3 rounded-full text-sm font-medium transition ${order.is_downloaded ? "bg-green-500 hover:bg-green-600 text-white" : "bg-gray-300 hover:bg-gray-400 text-gray-700"} ${updatingDownloadStatus[order.order_id] ? "opacity-50 cursor-not-allowed" : ""}`}
                          >
                            {updatingDownloadStatus[order.order_id]
                              ? "Updating..."
                              : order.is_downloaded
                                ? "Downloaded"
                                : "Not Downloaded"}
                          </button>
                        </td>
                      )}
                      <td className="px-2 py-4">{order.start_date}</td>
                      <td className="px-2 py-4">{order.end_date}</td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── CASHBACK / PROMOTIONAL TABLE ── */}
        {!loading && !error && orders.length > 0 && !isLoyaltyUser && (
          <table className="mt-4 border border-amber-500 rounded-lg min-w-full bg-white text-sm text-gray-950">
            {filteredOrders.length === 0 && (
              <tbody>
                <tr>
                  <td
                    colSpan={10}
                    className="font-bold text-md text-center text-gray-950 py-6"
                  >
                    No match found
                  </td>
                </tr>
              </tbody>
            )}
            <thead className="bg-amber-500 text-xs uppercase text-white text-center">
              <tr>
                <th className="py-3 px-5 border-b">SI No</th>
                <th className="py-3 px-5 border-b">Date</th>
                <th className="py-3 px-5 border-b">Order ID</th>

                <th className="py-3 px-5 border-b">Order Name</th>
                <th className="py-3 px-5 border-b">Order Type</th>
                <th className="py-3 px-5 border-b">
                  {orderType === "promotional" ? "Points" : "Amount"}
                </th>
                <th className="py-3 px-5 border-b">QR Quantity</th>
                {/* {orderType === "promotional" && <th className="py-3 px-5 border-b">Schema</th>} */}
                <th className="py-3 px-5 border-b">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-300">
              {filteredOrders.map((order, idx) => {
                const paymentEntry = payments?.data?.find(
                  (p) => p.order_id === order.order_id,
                );
                const paymentStatus = paymentEntry?.status;
                return (
                  <React.Fragment key={order.order_id}>
                    <tr
                      className="text-center hover:bg-amber-50 hover:text-gray-950 hover:font-semibold cursor-pointer"
                      onClick={() => handleMakePaymentClick(order.order_id)}
                    >
                      <td className="px-2 py-4">{idx + 1}</td>
                      <td className="px-2 py-4">{order.order_time}</td>
                      <td className="px-2 py-4">{order.order_id}</td>
                      <td className="px-2 py-4">
                        {order.order_remarks || "-"}
                      </td>
                      <td className="px-2 py-4">
                        {order.order_type?.charAt(0).toUpperCase() +
                          order.order_type?.slice(1)}
                      </td>

                      <td className="px-2 py-4">
                        {orderType === "promotional"
                          ? order.order_amount
                          : `₹${order.order_amount}`}
                      </td>
                      <td className="px-2 py-4">{order.qr_quantity}</td>
                      {/* {orderType === "promotional" && (
                        <td className="px-2 py-4">{order.breakdowns?.filter((b) => b.scheme).map((b) => b.scheme).join(", ") || "-"}</td>
                      )} */}
                      <td className="px-2 py-4">
                        {order.is_approved &&
                        order.is_active &&
                        order.is_expired === false ? (
                          <div className="flex justify-center items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeactivateClick(order);
                              }}
                              className="group bg-green-500 hover:bg-red-500 text-white py-1 px-3 rounded-full text-sm transition"
                            >
                              <span className="group-hover:hidden">
                                Activated
                              </span>
                              <span className="hidden group-hover:inline">
                                Deactivate
                              </span>
                            </button>
                            {order.is_downloaded !== true &&
                            order.order_download_url ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  dispatch(
                                    downloadOrderFile(
                                      order.order_id,
                                      userInfo.access_token,
                                    ),
                                  );
                                }}
                                className="py-1 px-3 rounded-full text-sm inline-flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white transition"
                              >
                                Click here to View
                              </button>
                            ) : order.is_downloaded &&
                              order.order_download_url ? (
                              <button
                                disabled
                                className="py-1 px-3 rounded-full text-sm inline-flex items-center gap-1 bg-gray-400 cursor-not-allowed text-white opacity-60"
                                title="Already downloaded"
                              >
                                <FaDownload />
                              </button>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleDownloadDropdown(order.order_id, e);
                                }}
                                className="py-1 px-3 rounded-full text-sm inline-flex items-center gap-1 bg-indigo-500 hover:bg-indigo-600 text-white transition"
                              >
                                <FaDownload />
                              </button>
                            )}
                          </div>
                        ) : order.is_approved && order.is_expired === false ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActivationData({
                                orderId: order.order_id,
                                startDate: "",
                                endDate: "",
                              });
                              setShowActivateModal(true);
                            }}
                            className="bg-blue-500 text-white rounded-full py-1 px-3 text-sm"
                          >
                            Activate
                          </button>
                        ) : order.is_expired === true ? (
                          // <button className="bg-red-500 text-white rounded-full py-1 px-3 text-sm">Expired</button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActivationData({
                                orderId: order.order_id,
                                startDate: "",
                                endDate: "",
                              });
                              setShowActivateModal(true);
                            }}
                            className="group bg-red-500 hover:bg-blue-500 text-white rounded-full py-1 px-3 text-sm transition"
                          >
                            <span className="group-hover:hidden">Expired</span>
                            <span className="hidden group-hover:inline">
                              Activate
                            </span>
                          </button>
                        ) : order.status === "In Processing" ? (
                          paymentStatus ? (
                            <span
                              className={`py-1 px-3 rounded-full text-sm font-semibold cursor-pointer ${
                                paymentStatus === "Payment Pending"
                                  ? "bg-yellow-100 text-yellow-700"
                                  : paymentStatus === "In process"
                                    ? "bg-yellow-500 text-white"
                                    : paymentStatus === "Completed"
                                      ? "bg-green-500 text-white"
                                      : paymentStatus === "Approval Pending"
                                        ? "bg-green-200 text-gray-700"
                                        : "bg-gray-300 text-gray-700"
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedOrderId(order.order_id);
                              }}
                            >
                              {paymentStatus}
                            </span>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedOrderId(order.order_id);
                                handleMakePaymentClick(order.order_id);
                              }}
                              className="bg-amber-500 text-white py-1 px-3 rounded-full text-sm"
                            >
                              Make Payment
                            </button>
                          )
                        ) : (
                          <div className="flex justify-center items-center gap-2">
                            <span className="bg-yellow-100 text-yellow-700 py-1 px-3 rounded-full text-sm">
                              Order Approval Pending
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                // navigate("/dashboard/orderRequest", { state: { editMode: true, order } });
                                navigate("/dashboard/orderRequest", {
                                  state: {
                                    editMode: true,
                                    order,
                                    type:
                                      order.order_type === "cashback"
                                        ? "cashback"
                                        : order.order_type === "promotional"
                                          ? "promotion"
                                          : order.order_type,
                                  },
                                });
                              }}
                              className="bg-amber-500 text-white py-1 px-3 rounded-full text-sm"
                            >
                              <FaEdit />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>

                    {selectedOrderId === order.order_id && (
                      <div className="fixed top-0 right-0 w-full sm:w-[600px] h-full bg-white border-l shadow-2xl z-50 overflow-y-auto p-6 flex flex-col">
                        <div className="flex justify-between items-center mb-2">
                          <h2 className="text-lg font-bold text-amber-600">
                            Order Number - {order.order_id}
                          </h2>
                          <button
                            onClick={() => setSelectedOrderId(null)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-6 w-6"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                        <div className="flex-grow overflow-y-auto space-y-4">
                          <div className="bg-amber-100 p-4 rounded-md space-y-2 border border-amber-600">
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-900 font-bold">
                                Order Date
                              </span>
                              <span className="text-md font-semibold text-gray-900">
                                {order.order_time}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-900 font-bold">
                                Denomination
                              </span>
                              <div className="text-right space-y-1">
                                {order.breakdowns?.length > 0 ? (
                                  order.breakdowns.map((item, i) => (
                                    <div
                                      key={i}
                                      className="text-md font-semibold text-gray-900"
                                    >
                                      ₹{item.amount} x {item.quantity} = ₹
                                      {item.amount * item.quantity}
                                    </div>
                                  ))
                                ) : (
                                  <div className="text-lg font-semibold text-gray-900">
                                    No denominations found
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-900 font-bold">
                                Order Amount
                              </span>
                              <span className="text-md font-semibold text-gray-900">
                                ₹{order.order_amount}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-900 font-bold">
                                Transaction Charge
                              </span>
                              <span className="text-md font-semibold text-gray-900">
                                ₹{paymentEntry?.extra_charge ?? 0}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-900 font-bold">
                                Total Amount
                              </span>
                              <span className="text-md font-semibold text-gray-900">
                                ₹
                                {order.order_amount +
                                  (paymentEntry?.extra_charge ?? 0)}
                              </span>
                            </div>
                            {order.is_active && (
                              <>
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-900 font-bold">
                                    Start Date
                                  </span>
                                  <span className="text-md font-semibold text-gray-900">
                                    {order.start_date}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-sm text-gray-900 font-bold">
                                    End Date
                                  </span>
                                  <span className="text-md font-semibold text-gray-900">
                                    {order.end_date}
                                  </span>
                                </div>
                              </>
                            )}
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-900 font-bold">
                                Payment Status
                              </span>
                              <span
                                className={`inline-block py-1 px-4 rounded-full text-sm font-semibold ${
                                  paymentStatus === "Successful"
                                    ? "bg-green-500 text-white"
                                    : paymentStatus === "In process"
                                      ? "bg-yellow-500 text-white"
                                      : paymentStatus === "Completed"
                                        ? "bg-green-500 text-white"
                                        : "bg-red-600 text-white"
                                }`}
                              >
                                {paymentStatus || "Not Paid"}
                              </span>
                            </div>
                          </div>

                          {paymentStatus === "Payment Pending" && (
                            <div className="bg-amber-100 p-4 rounded-2xl border border-amber-400 shadow-sm">
                              <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-3 border-amber-500">
                                Payment Receipt
                              </h3>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="flex flex-col">
                                  <label className="ml-2 mb-2 text-sm font-medium text-gray-950">
                                    Payment Type
                                  </label>
                                  <select
                                    value={paymentData.payment_type}
                                    onChange={(e) =>
                                      setPaymentData({
                                        ...paymentData,
                                        payment_type: e.target.value,
                                      })
                                    }
                                    className="w-full bg-white border border-gray-300 rounded-md px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                  >
                                    <option value="">
                                      Select Payment Type
                                    </option>
                                    <option value="UPI">UPI</option>
                                    <option value="Bank Transfer">
                                      Bank Transfer
                                    </option>
                                    <option value="Cash">Cash</option>
                                  </select>
                                </div>
                                {paymentData.payment_type ===
                                  "Bank Transfer" && (
                                  <div className="flex flex-col">
                                    <label className="ml-2 mb-2 text-sm font-medium">
                                      UTR Number{" "}
                                      <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="Enter UTR Number"
                                      value={paymentData.utr_number}
                                      onChange={(e) =>
                                        setPaymentData({
                                          ...paymentData,
                                          utr_number: e.target.value
                                            .replace(/[^a-zA-Z0-9]/g, "")
                                            .slice(0, 22),
                                        })
                                      }
                                      className="w-full border border-gray-300 rounded-md px-4 py-3 text-sm"
                                    />
                                  </div>
                                )}
                                {paymentData.payment_type === "UPI" && (
                                  <div className="flex flex-col">
                                    <label className="ml-2 mb-2 text-sm font-medium">
                                      UPI ID{" "}
                                      <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="e.g., name@bank"
                                      value={paymentData.upi_id || ""}
                                      onChange={(e) => {
                                        const v = e.target.value.trim();
                                        setPaymentData({
                                          ...paymentData,
                                          upi_id: v,
                                        });
                                        setUpiError(
                                          !validateUpi(v) && v !== ""
                                            ? "Invalid UPI ID"
                                            : "",
                                        );
                                      }}
                                      className={`w-full border rounded-md px-4 py-3 text-sm ${upiError ? "border-red-500" : "border-gray-300"}`}
                                    />
                                    {upiError && (
                                      <p className="text-red-500 text-sm mt-1">
                                        {upiError}
                                      </p>
                                    )}
                                  </div>
                                )}
                                {paymentData.payment_type === "Cash" && (
                                  <div className="flex flex-col">
                                    <label className="ml-2 mb-2 text-sm font-medium">
                                      Person Name{" "}
                                      <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                      type="text"
                                      placeholder="Enter Person's Name"
                                      value={paymentData.person_name || ""}
                                      onChange={(e) =>
                                        setPaymentData({
                                          ...paymentData,
                                          person_name: e.target.value.replace(
                                            /[^a-zA-Z]/g,
                                            "",
                                          ),
                                        })
                                      }
                                      className="w-full border border-gray-300 rounded-md px-4 py-3 text-sm"
                                    />
                                  </div>
                                )}
                                {paymentData.payment_type && (
                                  <div className="flex flex-col">
                                    <label className="ml-2 mb-2 text-sm font-medium">
                                      Payment Date{" "}
                                      <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                      type="datetime-local"
                                      value={paymentData.payment_date}
                                      onChange={(e) =>
                                        setPaymentData({
                                          ...paymentData,
                                          payment_date: e.target.value,
                                        })
                                      }
                                      className="w-full border border-gray-300 rounded-md px-4 py-3 text-sm"
                                    />
                                  </div>
                                )}
                              </div>
                              <div className="pt-6 flex justify-end">
                                <button
                                  onClick={handlePaymentSubmit}
                                  className="bg-indigo-600 hover:bg-green-600 text-white font-medium py-3 px-8 rounded-lg transition shadow-sm"
                                >
                                  Submit Payment
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Activate Modal ── */}
      {showActivateModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-amber-100 border border-amber-500 p-8 rounded-xl shadow-2xl w-full max-w-md mx-4">
            <h2 className="text-lg font-bold text-amber-600 mb-6 text-center">
              Activate QR Code
            </h2>
            {isLoyaltyUser ? (
              <div className="flex flex-col items-center justify-center gap-4 mb-6">
                <div className="w-full max-w-xs">
                  <label className="block mb-2 text-sm text-gray-950 font-medium text-center">
                    Start Date
                  </label>
                  <DatePicker
                    selected={
                      activationData.startDate instanceof Date
                        ? activationData.startDate
                        : activationData.startDate
                          ? new Date(activationData.startDate)
                          : null
                    }
                    onChange={(date) =>
                      setActivationData({ ...activationData, startDate: date })
                    }
                    dateFormat="dd-MM-yyyy"
                    className="ml-10 w-full p-2 border border-amber-500 rounded-md text-center"
                    placeholderText="dd/mm/yyyy"
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div className="w-full">
                  <label className="ml-1 block mb-2 text-sm text-gray-950 font-medium">
                    Start Date
                  </label>
                  <DatePicker
                    selected={
                      activationData.startDate instanceof Date
                        ? activationData.startDate
                        : activationData.startDate
                          ? new Date(activationData.startDate)
                          : null
                    }
                    onChange={(date) =>
                      setActivationData({ ...activationData, startDate: date })
                    }
                    dateFormat="dd-MM-yyyy"
                    className="w-full p-2 border border-amber-500 rounded-md text-center"
                    placeholderText="dd/mm/yyyy"
                    selectsStart
                    startDate={activationData.startDate}
                    endDate={activationData.endDate}
                  />
                </div>
                <div className="w-full">
                  <label className="ml-1 block mb-2 text-sm text-gray-950 font-medium">
                    End Date
                  </label>
                  <DatePicker
                    selected={
                      activationData.endDate instanceof Date
                        ? activationData.endDate
                        : activationData.endDate
                          ? new Date(activationData.endDate)
                          : null
                    }
                    onChange={(date) =>
                      setActivationData({ ...activationData, endDate: date })
                    }
                    dateFormat="dd-MM-yyyy"
                    className="w-full p-2 border border-amber-500 rounded-md text-center"
                    placeholderText="dd/mm/yyyy"
                    selectsEnd
                    startDate={activationData.startDate}
                    endDate={activationData.endDate}
                    minDate={activationData.startDate}
                  />
                </div>
              </div>
            )}
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowActivateModal(false)}
                className="px-4 py-2 rounded-md text-sm bg-red-500 text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleActivateSubmit}
                className="px-5 py-2 rounded-md text-sm bg-green-600 text-white"
              >
                Activate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Deactivate Modal ── */}
      {showDeactivateModal && deactivationOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h2 className="text-lg font-bold mb-4 text-red-600">
              Deactivate QR Codes
            </h2>
            <p className="mb-4 text-gray-700">
              Are you sure?{" "}
              <span className="font-semibold">
                Unused QR Codes: {deactivationOrder.unused_codes}
              </span>
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeactivateModal(false);
                  setDeactivationOrder(null);
                }}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 py-1 px-4 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeactivate}
                className="bg-red-600 hover:bg-red-700 text-white py-1 px-4 rounded"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Order Details Modal ── */}
      {showOrderDetailsModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-amber-500 to-amber-600 text-white p-6 flex items-center justify-between rounded-t-xl">
              <h2 className="text-2xl font-bold">Order Details</h2>
              <button
                onClick={() => {
                  setShowOrderDetailsModal(false);
                  setOrderDetails(null);
                }}
                className="text-white hover:text-gray-200 text-2xl font-bold"
              >
                x
              </button>
            </div>
            <div className="p-6">
              {loadingOrderDetails ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
                  <p className="mt-4 text-gray-600">Loading order details...</p>
                </div>
              ) : orderDetails ? (
                <div className="space-y-6">
                  <div className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200 rounded-xl p-5">
                    <h3 className="text-lg font-semibold text-gray-950 mb-4 flex items-center gap-2">
                      <span className="text-xl">📋</span> Order Information
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                      {[
                        ["Order ID", orderDetails.order_id],
                        ["Order Type", orderDetails.order_type],
                        ["Order Date", orderDetails.order_time || "-"],
                        ["Order Amount", `₹${orderDetails.order_amount || 0}`],
                        ["QR Quantity", orderDetails.qr_quantity || 0],
                        ["Status", orderDetails.status || "-"],
                        [
                          "Is Approved",
                          orderDetails.is_approved ? "Yes" : "No",
                        ],
                        ["Is Active", orderDetails.is_active ? "Yes" : "No"],
                        ["Start Date", orderDetails.start_date || "Not set"],
                        ["End Date", orderDetails.end_date || "Not set"],
                        ["Unused Codes", orderDetails.unused_codes || 0],
                        ["Used Codes", orderDetails.used_codes || 0],
                      ].map(([label, value]) => (
                        <div key={label}>
                          <label className="text-sm font-medium text-gray-600">
                            {label}
                          </label>
                          <p className="text-base font-semibold text-gray-900 capitalize">
                            {value}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                  {orderDetails.breakdowns?.length > 0 && (
                    <div className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200 rounded-xl p-5">
                      <h3 className="text-lg font-semibold text-gray-950 mb-4 flex items-center gap-2">
                        <span className="text-xl">📊</span> Order Breakdowns
                      </h3>
                      <div className="overflow-x-auto">
                        <table className="w-full bg-white border border-amber-300 rounded-lg text-sm">
                          <thead>
                            <tr className="bg-gradient-to-r from-amber-400 to-amber-500 text-white">
                              <th className="py-2 px-3 border-b border-amber-600 text-left">
                                Scheme
                              </th>
                              <th className="py-2 px-3 border-b border-amber-600 text-left">
                                Quantity
                              </th>
                              <th className="py-2 px-3 border-b border-amber-600 text-left">
                                Amount
                              </th>
                              <th className="py-2 px-3 border-b border-amber-600 text-left">
                                Remarks
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {orderDetails.breakdowns.map((b, i) => (
                              <tr key={i} className="hover:bg-amber-50">
                                <td className="py-2 px-3 border-b border-amber-200">
                                  {b.scheme || "-"}
                                </td>
                                <td className="py-2 px-3 border-b border-amber-200">
                                  {b.quantity || 0}
                                </td>
                                <td className="py-2 px-3 border-b border-amber-200">
                                  ₹{b.amount || 0}
                                </td>
                                <td className="py-2 px-3 border-b border-amber-200">
                                  {b.remarks || "-"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {orderDetails.gift_allotments?.length > 0 && (
                    <div className="bg-gradient-to-br from-amber-50 to-white border-2 border-amber-200 rounded-xl p-5">
                      <h3 className="text-lg font-semibold text-gray-950 mb-4 flex items-center gap-2">
                        <span className="text-xl">🎁</span> Gift Allotments (
                        {orderDetails.total_gift_allotments || 0})
                      </h3>
                      <div className="space-y-4">
                        {orderDetails.gift_allotments.map((gift, i) => (
                          <div
                            key={i}
                            className="bg-white border border-amber-200 rounded-lg p-4 hover:shadow-md transition"
                          >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {[
                                ["Gift Name", gift.gift_name || "-"],
                                ["QR Scheme", gift.qr_scheme || "-"],
                                ["QR Count", gift.qr_count || 0],
                                ["Is Live", gift.is_live ? "Yes" : "No"],
                                [
                                  "Bumper Offer",
                                  gift.is_additional_gift ? "Yes" : "No",
                                ],
                                ["Approved By", gift.approved_by || "-"],
                              ].map(([label, value]) => (
                                <div key={label}>
                                  <label className="text-sm font-medium text-gray-600">
                                    {label}
                                  </label>
                                  <p className="text-base font-semibold text-gray-900">
                                    {value}
                                  </p>
                                </div>
                              ))}
                              {gift.gift_image && (
                                <div className="md:col-span-2">
                                  <label className="text-sm font-medium text-gray-600">
                                    Gift Image
                                  </label>
                                  <img
                                    src={
                                      gift.gift_image.startsWith("http")
                                        ? gift.gift_image
                                        : `${axiosInstance.defaults.baseURL}${gift.gift_image}`
                                    }
                                    alt={gift.gift_name}
                                    className="mt-2 w-24 h-24 object-cover rounded-lg border border-amber-200"
                                    onError={(e) => {
                                      e.target.src =
                                        "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='96' height='96'><rect width='96' height='96' fill='%23ddd'/></svg>";
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-600">No order details available</p>
                </div>
              )}
            </div>
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex justify-end rounded-b-xl">
              <button
                onClick={() => {
                  setShowOrderDetailsModal(false);
                  setOrderDetails(null);
                }}
                className="bg-amber-500 hover:bg-amber-600 text-white py-2 px-6 rounded-lg font-semibold transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Download Options Modal ── */}
      {showDownloadDropdown && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowDownloadDropdown(null);
          }}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 download-options-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800">
                  Select Download Format
                </h2>
                <button
                  onClick={() => setShowDownloadDropdown(null)}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                >
                  x
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Order ID:{" "}
                <span className="font-semibold text-gray-800">
                  {showDownloadDropdown}
                </span>
              </p>
              {/* <div className="space-y-3">
                {[
                  { format: "pdf", Icon: AiOutlineFilePdf, label: "PDF", desc: "Download as PDF file", hover: "hover:bg-red-50 hover:text-red-700 hover:border-red-300", iconCls: "text-red-600", spinCls: "border-red-600" },
                  { format: "zip", Icon: AiOutlineFileZip, label: "ZIP", desc: "Download as ZIP archive", hover: "hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300", iconCls: "text-blue-600", spinCls: "border-blue-600" },
                  { format: "sticker", Icon: AiOutlineFileZip, label: "Sticker", desc: "Download with QR code stickers", hover: "hover:bg-amber-50 hover:text-amber-700 hover:border-amber-300", iconCls: "text-amber-600", spinCls: "border-amber-600" },
                ].map(({ format, Icon, label, desc, hover, iconCls, spinCls }) => (
                  <button key={format} type="button"
                    onClick={(e) => { e.stopPropagation(); handleDownloadOption(showDownloadDropdown, format); }}
                    disabled={downloadingFormat !== null}
                    className={`w-full text-left px-6 py-4 text-base text-gray-700 flex items-center gap-3 transition rounded-lg border-2 border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed ${hover}`}>
                    <Icon className={`text-2xl ${iconCls}`} />
                    <div className="flex-1">
                      <div className="font-semibold">{downloadingFormat === format ? `Downloading ${label}...` : label}</div>
                      <div className="text-xs text-gray-500">{desc}</div>
                    </div>
                    {downloadingFormat === format && <div className={`h-5 w-5 border-2 ${spinCls} border-t-transparent rounded-full animate-spin`}></div>}
                  </button>
                ))}
              </div> */}
              <div className="space-y-3">
                {[
                  {
                    format: "pdf",
                    alias: "pdf_file",
                    Icon: AiOutlineFilePdf,
                    label: "PDF",
                    desc: "Download as PDF file",
                    hover:
                      "hover:bg-red-50 hover:text-red-700 hover:border-red-300",
                    iconCls: "text-red-600",
                    spinCls: "border-red-600",
                  },
                  {
                    format: "zip",
                    alias: "zip_file",
                    Icon: AiOutlineFileZip,
                    label: "ZIP",
                    desc: "Download as ZIP archive",
                    hover:
                      "hover:bg-blue-50 hover:text-blue-700 hover:border-blue-300",
                    iconCls: "text-blue-600",
                    spinCls: "border-blue-600",
                  },
                  {
                    format: "sticker",
                    alias: "sticker_file",
                    Icon: AiOutlineFileZip,
                    label: "Sticker",
                    desc: "Download with QR code stickers",
                    hover:
                      "hover:bg-amber-50 hover:text-amber-700 hover:border-amber-300",
                    iconCls: "text-amber-600",
                    spinCls: "border-amber-600",
                  },
                ]
                  .filter(({ alias }) => hasModuleAccess(alias))
                  .map(
                    ({
                      format,
                      Icon,
                      label,
                      desc,
                      hover,
                      iconCls,
                      spinCls,
                    }) => (
                      <button
                        key={format}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadOption(showDownloadDropdown, format);
                        }}
                        disabled={downloadingFormat !== null}
                        className={`w-full text-left px-6 py-4 text-base text-gray-700 flex items-center gap-3 transition rounded-lg border-2 border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed ${hover}`}
                      >
                        <Icon className={`text-2xl ${iconCls}`} />
                        <div className="flex-1">
                          <div className="font-semibold">
                            {downloadingFormat === format
                              ? `Downloading ${label}...`
                              : label}
                          </div>
                          <div className="text-xs text-gray-500">{desc}</div>
                        </div>
                        {downloadingFormat === format && (
                          <div
                            className={`h-5 w-5 border-2 ${spinCls} border-t-transparent rounded-full animate-spin`}
                          />
                        )}
                      </button>
                    ),
                  )}

                {/* Show a message if no formats are available */}
                {["pdf_file", "zip_file", "sticker_file"].every(
                  (alias) => !hasModuleAccess(alias),
                ) && (
                  <div className="text-center py-6 text-gray-500 text-sm">
                    <p>No download formats are available for your account.</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Please contact your administrator.
                    </p>
                  </div>
                )}
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowDownloadDropdown(null)}
                  className="px-6 py-2 rounded-md text-sm bg-gray-400 hover:bg-gray-500 text-white transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Sticker Modal ── */}
      {showStickerModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div
            className={`bg-white border border-gray-300 p-8 rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto ${downloading ? "pointer-events-none opacity-75" : ""}`}
          >
            <h2 className="text-xl font-bold text-amber-600 mb-6 text-center">
              Configure Sticker — Order {stickerData.orderId}
            </h2>
            {downloadError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">
                  <strong>Error:</strong> {downloadError}
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* ── Left Panel ── */}
              <div className="space-y-6">
                {/* QR Design Selector */}
                <div>
                  <label className="block mb-3 text-sm font-medium text-gray-700">
                    Select QR Design *
                  </label>
                  {loadingQrDesigns ? (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-amber-600"></div>
                      <p className="mt-2 text-sm text-gray-600">
                        Loading QR designs...
                      </p>
                    </div>
                  ) : qrDesigns.length === 0 ? (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <p className="text-sm text-gray-600 mb-1">
                        No QR designs available
                      </p>
                      <p className="text-xs text-gray-500">
                        Please create a QR design first
                      </p>
                    </div>
                  ) : (
                    <div className="border-2 border-gray-300 rounded-lg p-4 max-h-64 overflow-y-auto">
                      <div className="grid grid-cols-1 gap-3">
                        {qrDesigns.map((design) => (
                          <div
                            key={design.id}
                            onClick={() => handleQrDesignSelect(design)}
                            className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${selectedQrDesign?.id === design.id ? "border-amber-600 bg-amber-50" : "border-gray-300 hover:border-amber-400 hover:bg-gray-50"}`}
                          >
                            <div className="flex items-center gap-3">
                              {design.qr_image && (
                                <img
                                  src={getMediaUrl(design.qr_image)}
                                  alt="QR Design"
                                  className="w-16 h-16 object-cover rounded border border-gray-200"
                                  onError={(e) => {
                                    e.target.src =
                                      "https://via.placeholder.com/64x64?text=No+Image";
                                  }}
                                />
                              )}
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-800">
                                  Design #{design.id}
                                </div>
                                <div className="text-xs text-gray-600 mt-1">
                                  Bottom: {design.bottom_percent || "0"}% |
                                  Right: {design.right_percent || "0"}%
                                </div>
                                <div className="text-xs text-gray-600">
                                  Rotation: {design.rotation_percent || "0"}° |
                                  Size: {design.qr_size_percent || "15"}%
                                </div>
                                {design.qr_count &&
                                  (() => {
                                    const parsed = parseQrCount(
                                      design.qr_count,
                                    );
                                    return parsed ? (
                                      <div className="text-xs font-semibold text-amber-700 mt-1">
                                        Grid: {parsed.rows} x {parsed.cols} ={" "}
                                        {parsed.total} QR codes
                                      </div>
                                    ) : null;
                                  })()}
                              </div>
                              {selectedQrDesign?.id === design.id && (
                                <div className="text-amber-600">
                                  <svg
                                    className="w-5 h-5"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Manual Upload */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                  <label className="block mb-3 text-sm font-medium text-gray-700">
                    Or Upload Base Image Manually
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none"
                  />
                  {imagePreview && !selectedQrDesign && (
                    <p className="text-xs text-gray-500 mt-2">
                      Image uploaded manually
                    </p>
                  )}
                </div>

                {/* Position Sliders */}
                {[
                  {
                    label: "Bottom Position",
                    key: "bottom_percent",
                    min: 0,
                    max: 100,
                    unit: "%",
                    hint: "Distance from bottom edge",
                  },
                  {
                    label: "Right Position",
                    key: "right_percent",
                    min: 0,
                    max: 100,
                    unit: "%",
                    hint: "Distance from right edge",
                  },
                  {
                    label: "Rotation",
                    key: "rotation_percent",
                    min: 0,
                    max: 360,
                    unit: "°",
                    hint: "QR code rotation angle",
                  },
                  {
                    label: "QR Size",
                    key: "qr_size_percent",
                    min: 1,
                    max: 100,
                    unit: "%",
                    hint: "QR code size as % of image diagonal",
                  },
                ].map(({ label, key, min, max, unit, hint }) => (
                  <div key={key}>
                    <label className="block mb-3 text-sm font-medium text-gray-700">
                      {label}: {stickerData[key]}
                      {unit}
                    </label>
                    <input
                      type="range"
                      min={min}
                      max={max}
                      value={stickerData[key]}
                      onChange={(e) =>
                        setStickerData((prev) => ({
                          ...prev,
                          [key]: e.target.value,
                        }))
                      }
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber-600"
                    />
                    <p className="text-xs text-gray-500 mt-2">{hint}</p>
                  </div>
                ))}

                <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
                  <label className="block mb-3 text-sm font-semibold text-gray-700">
                    QR Grid Layout
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    {/* ROWS input */}
                    <div>
                      <label className="block mb-1 text-xs font-medium text-gray-600">
                        Rows
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="50"
                        placeholder="e.g. 3"
                        value={qrRowsVal}
                        onChange={(e) => {
                          const rows = e.target.value.replace(/[^0-9]/g, "");
                          setStickerData((prev) => {
                            const prevCols =
                              (prev.qr_count || "").split("*")[1] ?? "";
                            return { ...prev, qr_count: `${rows}*${prevCols}` };
                          });
                        }}
                        className="w-full border-2 border-amber-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition"
                      />
                    </div>
                    {/* COLS input */}
                    <div>
                      <label className="block mb-1 text-xs font-medium text-gray-600">
                        Columns
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="50"
                        placeholder="e.g. 7"
                        value={qrColsVal}
                        onChange={(e) => {
                          const cols = e.target.value.replace(/[^0-9]/g, "");
                          setStickerData((prev) => {
                            const prevRows =
                              (prev.qr_count || "").split("*")[0] ?? "";
                            return { ...prev, qr_count: `${prevRows}*${cols}` };
                          });
                        }}
                        className="w-full border-2 border-amber-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition"
                      />
                    </div>
                  </div>

                  {/* Grid preview */}
                  {stickerData.qr_count &&
                    (() => {
                      const parsed = parseQrCount(stickerData.qr_count);
                      return parsed ? (
                        <div className="mt-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                              Grid Preview
                            </span>
                            <span className="text-xs font-bold text-amber-800 bg-amber-100 px-3 py-1 rounded-full border border-amber-300">
                              {parsed.rows} rows x {parsed.cols} cols ={" "}
                              {parsed.total} QR codes
                            </span>
                          </div>
                          {/* 
          gridTemplateColumns uses parsed.rows so that "rows" = number of cells ACROSS (horizontal).
          This way: rows=3 → 3 cells wide, cols=7 → 7 cells tall.
        */}
                          <div
                            className="grid gap-1 mx-auto w-fit"
                            style={{
                              gridTemplateColumns: `repeat(${Math.min(parsed.rows, 15)}, 1fr)`,
                            }}
                          >
                            {Array.from({
                              length:
                                Math.min(parsed.rows, 15) *
                                Math.min(parsed.cols, 10),
                            }).map((_, i) => (
                              <div
                                key={i}
                                className="w-4 h-4 bg-amber-500 rounded-sm flex items-center justify-center"
                              >
                                <svg viewBox="0 0 10 10" className="w-3 h-3">
                                  <rect
                                    x="1"
                                    y="1"
                                    width="3"
                                    height="3"
                                    fill="white"
                                  />
                                  <rect
                                    x="6"
                                    y="1"
                                    width="3"
                                    height="3"
                                    fill="white"
                                  />
                                  <rect
                                    x="1"
                                    y="6"
                                    width="3"
                                    height="3"
                                    fill="white"
                                  />
                                  <rect
                                    x="4"
                                    y="4"
                                    width="2"
                                    height="2"
                                    fill="white"
                                  />
                                </svg>
                              </div>
                            ))}
                          </div>
                          {(parsed.rows > 15 || parsed.cols > 10) && (
                            <p className="text-xs text-amber-600 mt-1 text-center italic">
                              Preview capped — actual: {parsed.rows} rows x{" "}
                              {parsed.cols} cols
                            </p>
                          )}
                        </div>
                      ) : null;
                    })()}
                </div>
                {/* Card Dimensions */}
                <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
                  <label className="block mb-3 text-sm font-semibold text-gray-700">
                    Card Size (cm)
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block mb-1 text-xs font-medium text-gray-600">
                        Card Width
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        min="0.1"
                        step="0.1"
                        placeholder="9.2"
                        value={stickerData.card_width}
                        onChange={(e) =>
                          setStickerData((prev) => ({
                            ...prev,
                            card_width: e.target.value.replace(/[^0-9.]/g, ""),
                          }))
                        }
                        className="w-full border-2 border-amber-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-xs font-medium text-gray-600">
                        Card Height
                      </label>
                      {/* <input
                        type="number"
                        min="0.1"
                        step="0.1"
                        placeholder="5.5"
                        value={stickerData.card_height}
                        onChange={(e) =>
                          setStickerData((prev) => ({
                            ...prev,
                            card_height: e.target.value.replace(/[^0-9.]/g, ""),
                          }))
                        }
                        className="w-full border-2 border-amber-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition"
                      /> */}

                      <input
                        type="text"
                        inputMode="decimal"
                        min="0.1"
                        step="0.1"
                        placeholder="5.5"
                        value={stickerData.card_height}
                        onChange={(e) =>
                          setStickerData((prev) => ({
                            ...prev,
                            card_height: e.target.value.replace(/[^0-9.]/g, ""),
                          }))
                        }
                        className="w-full border-2 border-amber-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition"
                      />
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 mt-2">
                    Optional — physical dimensions of each card in centimetres.
                  </p>
                </div>

                {/* Card Dimensions */}
                <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
                  <label className="block mb-3 text-sm font-semibold text-gray-700">
                    Page Size (cm)
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block mb-1 text-xs font-medium text-gray-600">
                        Page Width
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        min="0.1"
                        step="0.1"
                        placeholder="9.2"
                        value={stickerData.page_width}
                        onChange={(e) =>
                          setStickerData((prev) => ({
                            ...prev,
                            page_width: e.target.value.replace(/[^0-9.]/g, ""),
                          }))
                        }
                        className="w-full border-2 border-amber-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-xs font-medium text-gray-600">
                        Page Height
                      </label>

                      <input
                        type="text"
                        inputMode="decimal"
                        min="0.1"
                        step="0.1"
                        placeholder="5.5"
                        value={stickerData.page_height}
                        onChange={(e) =>
                          setStickerData((prev) => ({
                            ...prev,
                            page_height: e.target.value.replace(/[^0-9.]/g, ""),
                          }))
                        }
                        className="w-full border-2 border-amber-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition"
                      />
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 mt-2">
                    Optional — physical dimensions of each card in centimetres.
                  </p>
                </div>

                {/* ── Paper Size — FRONTEND ONLY, not sent to backend ── */}
                {/* <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                  <label className="block mb-3 text-sm font-semibold text-gray-700 flex items-center gap-2">
                    📄 Paper Size (auto-fills grid)
                    {paperWidth && paperHeight && (
                      <span className="ml-auto text-blue-700 font-bold bg-blue-100 px-2 py-0.5 rounded-full border border-blue-300 text-xs">
                        {paperWidth} x {paperHeight} mm
                      </span>
                    )}
                  </label>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {Object.entries(PAPER_SIZES).map(([key, preset]) => (
                      <button key={key} type="button" onClick={() => handlePaperSizeChange(key)}
                        className={`text-xs px-3 py-2 rounded-lg border-2 font-medium transition text-left ${
                          selectedPaperSize === key ? "border-blue-600 bg-blue-600 text-white" : "border-blue-300 bg-white text-blue-700 hover:bg-blue-50"
                        }`}>
                        {preset.label}
                        {preset.width && (
                          <span className={`block text-xs mt-0.5 ${selectedPaperSize === key ? "text-blue-100" : "text-gray-400"}`}>
                            {preset.width}x{preset.height}mm
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block mb-1 text-xs font-medium text-gray-600">Width (mm)</label>
                      <input type="number" min="1" placeholder="e.g. 210" value={paperWidth}
                        onChange={(e) => handleCustomDimensionChange("width", e.target.value)}
                        className="w-full border-2 border-blue-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition" />
                    </div>
                    <div>
                      <label className="block mb-1 text-xs font-medium text-gray-600">Height (mm)</label>
                      <input type="number" min="1" placeholder="e.g. 297" value={paperHeight}
                        onChange={(e) => handleCustomDimensionChange("height", e.target.value)}
                        className="w-full border-2 border-blue-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Select a preset to auto-fill rows & cols, or enter custom dimensions.
                    <span className="font-semibold text-blue-700"> Not sent to backend</span> — frontend helper only.
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800"><strong>Note:</strong> Adjust the sliders to position the QR code. See the preview on the right.</p>
                </div> */}
              </div>

              {/* ── Right Panel — Live Preview ── */}
              <div className="flex flex-col items-center justify-start border-2 border-gray-200 rounded-lg p-6 bg-gray-50">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">
                  Live Preview
                </h3>
                {paperWidth && paperHeight && (
                  <div className="mb-3 flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-700 w-full">
                    <span>📄</span>
                    <span className="font-semibold">
                      {selectedPaperSize !== "Custom"
                        ? PAPER_SIZES[selectedPaperSize]?.label
                        : "Custom"}
                      : {paperWidth} x {paperHeight} mm
                    </span>
                  </div>
                )}
                {imagePreview ? (
                  <div className="relative w-full max-w-sm">
                    <div className="relative inline-block w-full">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full rounded-lg shadow-md"
                        onLoad={(e) =>
                          setImageDimensions({
                            width: e.target.naturalWidth,
                            height: e.target.naturalHeight,
                          })
                        }
                      />
                      {(() => {
                        const diagonal = Math.sqrt(
                          imageDimensions.width ** 2 +
                            imageDimensions.height ** 2,
                        );
                        const qrSize =
                          (diagonal *
                            parseFloat(stickerData.qr_size_percent || 15)) /
                          100;
                        const scale = 384 / (imageDimensions.width || 1);
                        const qrSizePx = Math.max(
                          20,
                          Math.min(150, qrSize * scale),
                        );
                        const rightPct = parseFloat(
                          stickerData.right_percent || 0,
                        );
                        const bottomPct = parseFloat(
                          stickerData.bottom_percent || 0,
                        );
                        const naturalX =
                          imageDimensions.width * (1 - rightPct / 100) -
                          qrSize / 2;
                        const naturalY =
                          imageDimensions.height * (1 - bottomPct / 100) -
                          qrSize / 2;
                        return (
                          <div
                            className="absolute bg-blue-200 border-2 border-blue-500 rounded flex items-center justify-center transition-all duration-200"
                            style={{
                              width: `${qrSizePx}px`,
                              height: `${qrSizePx}px`,
                              left: `${naturalX * scale}px`,
                              top: `${naturalY * scale}px`,
                              transform: `rotate(${stickerData.rotation_percent}deg)`,
                            }}
                          >
                            <div className="text-center text-xs font-bold text-blue-600">
                              QR
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Bottom: {stickerData.bottom_percent}% | Right:{" "}
                      {stickerData.right_percent}% | Rotation:{" "}
                      {stickerData.rotation_percent}° | Size:{" "}
                      {stickerData.qr_size_percent}%
                      {stickerData.qr_count && (
                        <span className="ml-1 text-amber-600 font-semibold">
                          | Grid: {qrRowsVal} x {qrColsVal}
                        </span>
                      )}
                      {paperWidth && paperHeight && (
                        <span className="ml-1 text-blue-600 font-semibold">
                          | Paper: {paperWidth}x{paperHeight}mm
                        </span>
                      )}
                    </p>
                  </div>
                ) : (
                  <div className="w-full max-w-sm aspect-square bg-white border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                    <p className="text-gray-400 text-sm">
                      Upload an image to see preview
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-4 mt-8">
              <button
                onClick={() => {
                  if (downloading) {
                    toast.warning("Please wait for the download to complete");
                    return;
                  }
                  setShowStickerModal(false);
                  setImagePreview(null);
                  setImageDimensions({ width: 0, height: 0 });
                  setSelectedQrDesign(null);
                  setQrDesigns([]);
                  setSelectedPaperSize("Custom");
                  setPaperWidth("");
                  setPaperHeight("");
                }}
                disabled={downloading}
                className="px-6 py-2 rounded-md text-sm bg-gray-400 hover:bg-gray-500 text-white transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleStickerSubmit}
                disabled={
                  !stickerData.base_image || !stickerData.orderId || downloading
                }
                className="px-6 py-2 rounded-md text-sm bg-amber-600 hover:bg-amber-700 text-white transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {downloading ? (
                  <>
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Generating...</span>
                  </>
                ) : (
                  "Generate Sticker"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default MyOrders;
